import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { AxiosError } from "axios";

import {
  Button,
  Dialog,
  SelectInput,
  TextInput,
} from "fe-shared/src/components";
import { CheckIcon } from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import useValidation from "fe-shared/src/hooks/useValidation";

import { COMMENT_LENGTH_LIMIT } from "consts";

import {
  errorMessages,
  getInitialContent,
  getValidationConfig,
} from "../modules/config";
import * as api from "../modules/api";

import styles from "../OverrideRegYear.module.scss";

interface Props {
  isActive: boolean;
  transactionId: number;
  availableRegYears: string[];
  onClose: () => void;
}

const OverrideRegYearDialog = ({
  isActive,
  availableRegYears,
  transactionId,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const availableRegYearOptions = useMemo(
    () =>
      availableRegYears.map((r) => ({
        label: r,
        value: r,
      })),
    [availableRegYears]
  );

  const {
    errors,
    values,
    isValid,
    validateAll,
    validateValue,
    updateValue,
  } = useValidation(getValidationConfig(), getInitialContent(), errorMessages);

  const [overrideRegYear, overrideRegYearResult] = useMutation<
    unknown,
    AxiosError<ApiError>,
    api.OverrideRegYearParams
  >(api.overrideRegYear, {
    throwOnError: true,
    onSuccess: () => {
      document.location.reload();
      onClose();
    },
  });
  const { isLoading } = overrideRegYearResult;
  const isCommentTooLong = values.comment.length > COMMENT_LENGTH_LIMIT;

  const isSubmitDisabled =
    !!Object.keys(errors).length || isCommentTooLong || isLoading;

  const isCommentError = !!errors.comment || isCommentTooLong;

  const commentErrorText = useMemo(() => {
    if (isCommentTooLong) {
      const characterLimitText = `${values.comment.length.toLocaleString()}/${COMMENT_LENGTH_LIMIT.toLocaleString()}`;

      return t("validation.commentMaxLength", { characterLimitText });
    }

    return errors.comment;
  }, [errors.comment, isCommentTooLong, t, values.comment.length]);

  const onSubmit = async () => {
    validateAll();
    if (isValid) {
      overrideRegYear({
        transactionId,
        regulatoryYear: Number(values.regYear),
        comment: values.comment,
      });
    }
  };

  const actions: React.ReactNode[] = [
    <Button
      key="1"
      id="override-reg-year-submit"
      variant="filled"
      iconComponent={CheckIcon}
      onClick={onSubmit}
      disabled={isSubmitDisabled}
    >
      {t("submit")}
    </Button>,
    <Button
      key="2"
      id="override-reg-year-cancel"
      variant="outlined"
      onClick={onClose}
    >
      {t("cancel")}
    </Button>,
  ];

  return (
    <Dialog
      modal
      isActive={isActive}
      onClose={onClose}
      title={t("transactions.details.regYear.dialogTitle")}
      actions={actions}
    >
      <SelectInput
        id="override-reg-year-dropdown"
        title={t("transactions.details.regYear.title")}
        placeholder={t("transactions.details.regYear.dropdownPlaceholder")}
        required
        value={values.regYear}
        isError={!!errors.regYear}
        errorText={errors.regYear}
        options={availableRegYearOptions}
        onBlur={() => validateValue("regYear")}
        onChange={(event, value) => {
          event.preventDefault();
          updateValue("regYear", Number(value));
        }}
        labelClassName={styles.label}
        wrapperClassName={styles.inputWrapper}
      />
      <TextInput
        id="override-reg-year-comment"
        title={t("transactions.details.regYear.commentTitle")}
        placeholder={t("transactions.details.regYear.commentPlaceholder")}
        required
        isMultiline
        isError={isCommentError}
        errorText={commentErrorText}
        value={values.comment}
        onBlur={() => validateValue("comment")}
        onChange={(event, value) => updateValue("comment", value)}
        labelClassName={styles.label}
      />
    </Dialog>
  );
};

export default OverrideRegYearDialog;
